import React, { useRef, useState, useEffect, forwardRef } from 'react'
import type { VariantProps } from 'class-variance-authority'
import { cva } from 'class-variance-authority'
import Tooltip from './Tooltip'

export const paragraph = cva(['font-sans', 'leading-[150%]'], {
    variants: {
        color: {
            black: 'text-textPrimary',
            brand: 'text-textBrand',
            darkGrey: 'text-textSecondary',
            lightGrey: 'text-textTertiary',
            light: 'text-textOnColor',
            positive: 'text-textPositive',
            negative: 'text-textNegative',
            negativeSecondary: 'text-textNegativeSecondary',
            warning: 'text-textWarning',
            purple: 'text-textExtraPurple'
        },
        size: {
            1: 'text-xxs',
            2: 'text-xs',
            3: 'text-sm',
            4: 'text-base',
            5: 'text-lg',
            6: 'text-xl',
            7: 'text-2xl'
        },
        variant: {
            regular: 'font-normal',
            medium: 'font-medium',
            semiBold: 'font-semibold',
            bold: 'font-bold'
        }
    },
    defaultVariants: {
        color: 'black',
        variant: 'regular',
        size: 4
    }
})

export type ParagraphProps = Omit<React.HTMLAttributes<HTMLParagraphElement>, 'color'> & VariantProps<typeof paragraph>

const Paragraph = forwardRef<HTMLParagraphElement, ParagraphProps>(
    ({ className, color, variant, size, children, ...props }, forwardedRef) => {
        const [isTruncated, setIsTruncated] = useState(false)
        const innerRef = useRef<HTMLParagraphElement>(null)

        const paragraphRef = forwardedRef || innerRef

        useEffect(() => {
            const paragraph = 'current' in paragraphRef ? paragraphRef.current : null
            if (paragraph) {
                setIsTruncated(paragraph.scrollWidth > paragraph.clientWidth)
            }
        }, [children, paragraphRef])

        return (
            <Tooltip
                isActive={isTruncated}
                trigger={
                    <p
                        ref={paragraphRef}
                        className={paragraph({
                            color,
                            variant,
                            size,
                            className
                        })}
                        {...props}
                    >
                        {children}
                    </p>
                }
                content={children}
            />
        )
    }
)

export default Paragraph
